/**
 * Packaging Routing Sorter - Chuẩn Hóa Thứ Tự Công Đoạn Bao Bì
 * 
 * Quy tắc bất biến trong công nghệ in & bao bì công nghiệp:
 * 1. TRƯỚC IN: Cắt xén, Ghi bản kẽm CTP
 * 2. IN: In Offset, In Flexo, In KTS
 * 3. SAU IN: Cán màng -> Tráng phủ -> In lụa UV -> Ép kim -> Thúc nổi
 * 4. THÀNH PHẨM: Bồi sóng (410) -> Bế định hình (420) -> Cắt TP (430) -> Gấp dán (440) -> Dán window (450) -> KCS (480) -> Đóng gói (490)
 * 
 * BẮT BUỘC: Bồi sóng (410) PHẢI NẰM TRƯỚC Bế (420) vì phải bồi tấm sóng xong mới đưa lên máy bế!
 */

export interface ProcessSortable {
  id?: string;
  name?: string;
  group?: string | null;
  sortOrder?: number | null;
  code?: string | null;
}

export function getProcessTechnologicalWeight(process: ProcessSortable | null | undefined): number {
  if (!process) return 500;

  const name = (process.name || '').toLowerCase();
  const group = (process.group || '').toUpperCase();
  const explicitOrder = process.sortOrder;

  // 1. Phân nhóm TRUOC_IN (100 - 199)
  if (group === 'TRUOC_IN' || name.includes('trước in') || name.includes('ra kẽm') || name.includes('chế bản')) {
    if (name.includes('xén') || name.includes('cắt phôi')) return 110;
    return 120;
  }

  // 2. Phân nhóm IN (200 - 299)
  if (group === 'IN' || name.includes('in offset') || name.includes('in flexo') || name.includes('in kts') || name.includes('in 4 màu') || name.includes('in cmyk')) {
    if (name.includes('offset')) return 210;
    if (name.includes('flexo')) return 220;
    if (name.includes('kts') || name.includes('kỹ thuật số')) return 230;
    return 240;
  }

  // 3. Phân nhóm SAU_IN (300 - 399)
  if (
    group === 'SAU_IN' ||
    name.includes('cán') ||
    name.includes('tráng phủ') ||
    name.includes('uv') ||
    name.includes('lụa') ||
    name.includes('ép kim') ||
    name.includes('thúc nổi')
  ) {
    if (name.includes('cán màng') || name.includes('cán bóng') || name.includes('cán mờ')) return 310;
    if (name.includes('tráng phủ') || name.includes('vecni') || name.includes('dầu bóng')) return 320;
    if (name.includes('uv') || name.includes('in lụa')) return 330;
    if (name.includes('ép kim') || name.includes('ép nhũ')) return 340;
    if (name.includes('thúc nổi') || name.includes('dập nổi') || name.includes('dập chìm')) return 350;
    return 360;
  }

  // 4. Phân nhóm THÀNH PHẨM (400 - 499)
  // ĐẶC BIỆT: Bồi sóng (410) < Bế (420) < Cắt (430) < Dán (440) < Đóng gói (490)
  if (name.includes('bồi') || name.includes('bồi sóng') || name.includes('bồi carton') || name.includes('bồi duplex')) {
    return 410;
  }
  if (name.includes('bế') || name.includes('dập định hình') || name.includes('bế demi')) {
    return 420;
  }
  if (name.includes('cắt xén') || name.includes('cắt tp') || name.includes('xén thành phẩm')) {
    return 430;
  }
  if (name.includes('dán') || name.includes('gấp dán') || name.includes('dán cạnh') || name.includes('dán đáy')) {
    if (name.includes('window') || name.includes('cửa sổ')) return 450;
    return 440;
  }
  if (name.includes('kcs') || name.includes('kiểm tra')) {
    return 480;
  }
  if (name.includes('đóng gói') || name.includes('đóng thùng') || name.includes('pallet')) {
    return 490;
  }

  // Fallback: Sử dụng sortOrder của DB nếu có
  if (typeof explicitOrder === 'number' && explicitOrder > 0) {
    // Nếu explicitOrder thuộc khoảng 1-90, map vào dải tương ứng
    if (explicitOrder <= 20) return 200 + explicitOrder;
    if (explicitOrder <= 35) return 300 + explicitOrder;
    if (explicitOrder <= 60) return 400 + explicitOrder;
    return 500 + explicitOrder;
  }

  return 500;
}

/**
 * Sắp xếp danh sách processId theo chuẩn chuỗi công nghệ bao bì
 */
export function sortPackagingProcessIds(
  processIds: string[],
  lookupMap: Map<string, ProcessSortable> | { [id: string]: ProcessSortable } | Array<{ id: string; original?: any }>
): string[] {
  const getObj = (id: string): ProcessSortable | undefined => {
    if (lookupMap instanceof Map) return lookupMap.get(id);
    if (Array.isArray(lookupMap)) {
      const found = lookupMap.find((item) => item.id === id);
      return found?.original || found;
    }
    return (lookupMap as any)[id];
  };

  return [...processIds].sort((aId, bId) => {
    const aProc = getObj(aId);
    const bProc = getObj(bId);
    const wA = getProcessTechnologicalWeight(aProc);
    const wB = getProcessTechnologicalWeight(bProc);
    if (wA !== wB) return wA - wB;
    return (aProc?.name || '').localeCompare(bProc?.name || '');
  });
}

/**
 * Sắp xếp mảng processConfigs hoặc productProcesses
 */
export function sortPackagingProcessConfigs<T extends { processId?: string; id?: string; process?: any }>(
  configs: T[],
  lookupMap: Map<string, ProcessSortable> | { [id: string]: ProcessSortable } | Array<{ id: string; original?: any }>
): T[] {
  const getObj = (id: string, item?: T): ProcessSortable | undefined => {
    if (item?.process) return item.process;
    if (lookupMap instanceof Map) return lookupMap.get(id);
    if (Array.isArray(lookupMap)) {
      const found = lookupMap.find((opt) => opt.id === id);
      return found?.original || found;
    }
    return (lookupMap as any)[id];
  };

  return [...configs].sort((a, b) => {
    const aId = a.processId || a.id || '';
    const bId = b.processId || b.id || '';
    const aProc = getObj(aId, a);
    const bProc = getObj(bId, b);
    const wA = getProcessTechnologicalWeight(aProc);
    const wB = getProcessTechnologicalWeight(bProc);
    if (wA !== wB) return wA - wB;
    return (aProc?.name || '').localeCompare(bProc?.name || '');
  });
}
